import type { TimerAudioEvent, TimerAudioSound, TimerAudioVolume } from './timerAudioConfig';

export interface TimerTransitionSnapshot {
  level: number;
  isRunning: boolean;
  isBreak: boolean;
  remainingSeconds: number;
  nextIsBreak?: boolean;
  nextLevel?: number;
  startedAt?: string | null;
}

export type TimerTransitionSound = 'single' | TimerAudioSound;
export type TimerTransitionEvent = 'single' | TimerAudioEvent;

export interface TimerTransitionState {
  previous: TimerTransitionSnapshot | null;
  silentBaseline?: boolean;
  openingStartObserved: boolean;
  consumedThresholds: number[];
}

export function createTimerTransitionState(): TimerTransitionState {
  return {
    previous: null,
    openingStartObserved: false,
    consumedThresholds: [],
  };
}

export function reduceTimerTransition(
  state: TimerTransitionState,
  next: TimerTransitionSnapshot,
): { state: TimerTransitionState; event: TimerTransitionEvent | null } {
  const previous = state.silentBaseline ? null : state.previous;
  const openingStartObserved =
    state.openingStartObserved || !!next.startedAt || (next.isRunning && !next.isBreak);

  const consumedThresholds =
    state.previous?.level === next.level ? [...state.consumedThresholds] : [];
  let event: TimerTransitionEvent | null = null;

  if (previous && next.isRunning) {
    if (previous.isRunning && next.level > previous.level) {
      event = next.isBreak ? 'break-start' : previous.isBreak ? 'break-end' : 'level-up';
    } else if (
      !state.openingStartObserved &&
      !previous.startedAt &&
      !previous.isBreak &&
      !next.isBreak &&
      previous.level === next.level &&
      !previous.isRunning
    ) {
      event = 'single';
    }
  }

  const isFinalLevel = (snapshot: TimerTransitionSnapshot) =>
    !snapshot.isBreak && snapshot.nextLevel == null && snapshot.nextIsBreak === false;

  if ((next.nextIsBreak || isFinalLevel(next)) && !next.isBreak) {
    for (const threshold of [30, 10] as const) {
      if (next.remainingSeconds <= threshold && !consumedThresholds.includes(threshold)) {
        // Consume even a silent baseline or paused crossing; added time must not replay it.
        consumedThresholds.push(threshold);
        if (
          previous?.level === next.level &&
          previous.isRunning &&
          next.isRunning &&
          !previous.isBreak &&
          (previous.nextIsBreak || isFinalLevel(previous)) &&
          previous.remainingSeconds > threshold &&
          next.remainingSeconds > 0
        ) {
          if (!event || event.endsWith('-30')) {
            event = isFinalLevel(next)
              ? threshold === 30
                ? 'final-30'
                : 'final-10'
              : threshold === 30
                ? 'before-break-30'
                : 'before-break-10';
          }
        }
      }
    }
  }

  if (isFinalLevel(next) && next.remainingSeconds <= 0 && !consumedThresholds.includes(0)) {
    // Final expiry auto-pauses on the server; consume silent baselines as well.
    consumedThresholds.push(0);
    if (
      previous?.level === next.level &&
      isFinalLevel(previous) &&
      previous.isRunning &&
      previous.remainingSeconds > 0
    ) {
      event = 'final-end';
    }
  }

  return {
    state: { previous: next, openingStartObserved, consumedThresholds },
    event,
  };
}

interface AudioParamLike {
  setValueAtTime(value: number, startTime: number): void;
  exponentialRampToValueAtTime(value: number, endTime: number): void;
}

interface AudioNodeLike {
  connect(destination: unknown): unknown;
}

interface AudioContextLike {
  currentTime: number;
  state: string;
  destination: unknown;
  createOscillator(): AudioNodeLike & {
    type: OscillatorType;
    frequency: Pick<AudioParamLike, 'setValueAtTime'>;
    start(when?: number): void;
    stop(when?: number): void;
  };
  createGain(): AudioNodeLike & { gain: AudioParamLike };
  resume(): Promise<void>;
}

type AudioContextFactory = () => AudioContextLike;

function browserAudioContext(): AudioContextLike {
  const AudioContextConstructor = window.AudioContext;
  return new AudioContextConstructor();
}

function scheduleChime(context: AudioContextLike, offsetSeconds: number, frequency = 660): void {
  const startTime = context.currentTime + offsetSeconds;
  const oscillator = context.createOscillator();
  const gain = context.createGain();

  oscillator.type = 'triangle';
  oscillator.frequency.setValueAtTime(frequency, startTime);
  gain.gain.setValueAtTime(0.0001, startTime);
  gain.gain.exponentialRampToValueAtTime(0.28, startTime + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, startTime + 0.22);
  oscillator.connect(gain);
  gain.connect(context.destination);
  oscillator.start(startTime);
  oscillator.stop(startTime + 0.24);
}

interface AudioFileLike {
  volume: number;
  play(): Promise<void>;
}

type AudioFileFactory = (url: string) => AudioFileLike;

const blindsUpUrl = new URL('../assets/audio/Audio_BlindsUp.wav', import.meta.url).href;
const countdownUrl = new URL('../assets/audio/Audio_CD.wav', import.meta.url).href;

export async function playTimerTransitionSound(
  sound: TimerTransitionSound,
  createContext: AudioContextFactory = browserAudioContext,
  createAudio: AudioFileFactory = (url) => new Audio(url),
  volume: TimerAudioVolume = 0.5,
): Promise<void> {
  try {
    if (sound !== 'single') {
      if (volume === 0) return;
      const audio = createAudio(sound === 'blinds-up' ? blindsUpUrl : countdownUrl);
      audio.volume = volume;
      await audio.play();
      return;
    }
    const context = createContext();
    if (context.state === 'suspended') await context.resume();
    scheduleChime(context, 0);
  } catch {
    // Audio output must never interrupt the TV timer display.
  }
}
